import { useCallback, useMemo } from 'react';
import { isEqual } from 'lodash-es';
import { useForm } from '@frontend/design-system';
import { useGetCalendarV3QueriesData } from '../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useCalendarStore } from '../../stores/calendar.store';

export const useFilterPanelForm = () => {
  const {
    calendarApptStatuses,
    calendarApptTypeIds,
    calendarProviderIds,
    calendarOperatories,
    calendarInsuranceStatuses,
    setCalendarFilters,
    resetCalendarFilters,
  } = useCalendarStore([
    'calendarApptStatuses',
    'calendarApptTypeIds',
    'calendarProviderIds',
    'calendarOperatories',
    'calendarInsuranceStatuses',
    'setCalendarFilters',
    'resetCalendarFilters',
  ]);
  const formField = useForm({
    fields: {
      apptStatuses: { type: 'checklist', value: [] },
      apptTypes: { type: 'checklist', value: [] },
      insuranceStatuses: { type: 'checklist', value: [] },
      operatories: { type: 'checklist', value: [] },
      providers: { type: 'checklist', value: [] },
    },
  });

  const syncFilters = useCallback(
    (
      fieldsToUpdate?: Partial<{
        apptStatuses: string[];
        apptTypes: string[];
        insuranceStatuses: string[];
        operatories: string[];
        providers: string[];
      }>
    ) => {
      // If no specific fields provided, sync all fields with store values
      if (!fieldsToUpdate) {
        fieldsToUpdate = {
          apptStatuses: calendarApptStatuses,
          apptTypes: calendarApptTypeIds,
          insuranceStatuses: calendarInsuranceStatuses,
          operatories: calendarOperatories,
          providers: calendarProviderIds,
        };
      }

      const fieldsToReset: Partial<{
        apptStatuses: string[];
        apptTypes: string[];
        insuranceStatuses: string[];
        operatories: string[];
        providers: string[];
      }> = {};

      Object.keys(fieldsToUpdate).forEach((key) => {
        fieldsToReset[key as keyof typeof fieldsToReset] = [];
      });

      // NOTE: due to issue on useForm we were not able to reset/ set field values directly
      // so we are seeding the values instead
      // This updates only the fields provided in the argument with their respective values
      formField.seedValues(fieldsToReset);
      formField.seedValues(fieldsToUpdate);
    },
    [
      calendarApptStatuses,
      calendarApptTypeIds,
      calendarInsuranceStatuses,
      calendarOperatories,
      calendarProviderIds,
      formField,
    ]
  );

  const applyFilters = () => {
    setCalendarFilters({
      apptTypeIds: formField.values.apptTypes ?? [],
      providerIds: formField.values.providers ?? [],
      apptStatuses: formField.values.apptStatuses ?? [],
      insuranceStatuses: formField.values.insuranceStatuses ?? [],
      operatories: formField.values.operatories ?? [],
    });
  };

  const resetFilters = resetCalendarFilters;

  const apptStatusesField = formField.getFieldProps('apptStatuses');
  const apptStatusesFilterCount = apptStatusesField.value.length;
  const apptTypesField = formField.getFieldProps('apptTypes');
  const apptTypesFilterCount = apptTypesField.value.length;
  const insuranceStatusesField = formField.getFieldProps('insuranceStatuses');
  const insuranceStatusesFilterCount = insuranceStatusesField.value.length;
  const operatoriesField = formField.getFieldProps('operatories');
  const operatoriesFilterCount = operatoriesField.value.length;
  const providersField = formField.getFieldProps('providers');
  const providersFilterCount = providersField.value.length;
  const isFiltersChanged =
    !isEqual(apptStatusesField.value, calendarApptStatuses) ||
    !isEqual(apptTypesField.value, calendarApptTypeIds) ||
    !isEqual(providersField.value, calendarProviderIds) ||
    !isEqual(operatoriesField.value, calendarOperatories) ||
    !isEqual(insuranceStatusesField.value, calendarInsuranceStatuses);

  const totalFilterCount =
    apptStatusesFilterCount +
    apptTypesFilterCount +
    insuranceStatusesFilterCount +
    operatoriesFilterCount +
    providersFilterCount;

  //#region Applied Filters related object and functions

  const { filterLists } = useGetCalendarV3QueriesData();

  const providerLabelMap = useMemo(
    () => new Map(filterLists.providers.map((provider) => [provider.value, provider.label])),
    [filterLists]
  );

  const apptTypeLabelMap = useMemo(
    () => new Map(filterLists.appointmentTypes.map((apptType) => [apptType.value, apptType.label])),
    [filterLists]
  );

  const selectedFilters = useMemo(() => {
    const createFilterItems = (type: string, values: string[], labelMap?: Map<string, string>) =>
      values.map((value) => ({
        type,
        label: labelMap?.get(value) || value,
        value,
      }));

    return [
      ...createFilterItems('apptStatuses', apptStatusesField.value),
      ...createFilterItems('apptTypes', apptTypesField.value, apptTypeLabelMap),
      ...createFilterItems('providers', providersField.value, providerLabelMap),
      ...createFilterItems('operatories', operatoriesField.value),
      ...createFilterItems('insuranceStatuses', insuranceStatusesField.value),
    ];
  }, [
    apptStatusesField.value,
    apptTypesField.value,
    providersField.value,
    operatoriesField.value,
    insuranceStatusesField.value,
    apptTypeLabelMap,
    providerLabelMap,
  ]);

  const removeFilter = useMemo(
    () => (type: string, value: string) => {
      const currentValues = formField.values[type as keyof typeof formField.values] as string[];
      const updatedValues = currentValues?.filter((currValue) => currValue !== value) || [];
      syncFilters({ [type]: updatedValues });
    },
    [formField, syncFilters]
  );

  //#endregion

  return {
    formField,
    syncFilters,
    applyFilters,
    resetFilters,
    isFiltersChanged,
    filterCounts: {
      apptStatuses: apptStatusesFilterCount,
      apptTypes: apptTypesFilterCount,
      insuranceStatuses: insuranceStatusesFilterCount,
      operatories: operatoriesFilterCount,
      providers: providersFilterCount,
      total: totalFilterCount,
    },
    appliedFilters: {
      selectedFilters,
      removeFilter,
    },
  };
};
